import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Text } from 'theme-ui';
import InputWithPlaceholder from '~/components/Generic/Form/InputWithPlaceholder';
import useCTAStyle from '~/hooks/components/use-cta-style';

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  organization?: string;
  title?: string;
  sportOfInterest?: string;
};
const TeamSalesForm = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>();

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const onSubmit = async (data: FormValues) => {
    setSubmitError(null);
    try {
      setIsSubmitting(true);
      // Check if profile exists
      const profileExistsResponse = await fetch('/api/check-profile', {
        method: 'POST',
        body: JSON.stringify({ email: data.email }),
      });
      let id = null;
      if (profileExistsResponse.status === 404) {
        const profileResponse = await fetch('/api/create-profile', {
          method: 'POST',
          body: JSON.stringify(data),
        });
        if (!profileResponse.ok) {
          setSubmitError('Failed to create profile');
          throw new Error('Failed to create profile');
        }
        const profileData = await profileResponse.json();
        id = profileData.data.id;
      } else {
        const profileData = await profileExistsResponse.json();
        await fetch('/api/update-profile', {
          method: 'PATCH',
          body: JSON.stringify({ ...data, id: profileData.id }),
        });
        id = profileData.id;
      }
      const listResponse = await fetch('/api/add-profile-to-list', {
        method: 'POST',
        body: JSON.stringify({ id }),
      });
      if (!listResponse.ok) {
        setSubmitError('Failed to add profile to list');
        throw new Error('Failed to add profile to list');
      } else {
        setSubmitSuccess(true);
        reset();
      }
    } catch (error) {
      setSubmitError('An error occurred. Please try again later.');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
    timeoutRef.current = setTimeout(() => {
      setSubmitError(null);
    }, 5000);
  };
  const CTAStyle = useCTAStyle({
    variant: 'Filled',
    size: 'Medium',
    baseColor: 'Black',
    disableDisabledStyle: false,
  });

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (submitSuccess) {
      timeout = setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [submitSuccess]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Box
      sx={{
        maxWidth: '51rem',
        mx: 'auto',
        pt: ['1.6rem', '2.4rem'],
        px: '1.6rem',
        position: 'relative',
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)} sx={{ display: 'grid', gap: ['1.6rem', '2.4rem'] }}>
        <InputWithPlaceholder placeholder="First Name*" theme="light" {...register('firstName')} required />
        <InputWithPlaceholder placeholder="Last Name*" theme="light" {...register('lastName')} required />
        <InputWithPlaceholder
          placeholder="Email*"
          theme="light"
          {...register('email', {
            pattern: {
              value: /^[\w-.+]+@([\w-]+.)+[\w-]{2,4}$/gi,
              message: 'Invalid email',
            },
          })}
          required
          error={errors.email?.message}
        />
        <InputWithPlaceholder placeholder="Organization*" theme="light" {...register('organization')} required />
        <InputWithPlaceholder placeholder="Title*" theme="light" {...register('title')} required />
        <InputWithPlaceholder
          placeholder="Sport of interest*"
          theme="light"
          {...register('sportOfInterest')}
          required
        />
        <InputWithPlaceholder placeholder="Phone" theme="light" {...register('phone')} />
        {submitSuccess ? (
          <Text
            sx={{
              justifySelf: 'center',
              fontSize: '1.6rem',
            }}
          >
            Thanks, we'll be in touch soon.
          </Text>
        ) : (
          <button
            sx={{
              ...CTAStyle,
              justifySelf: 'center',
              px: '2.4rem',
            }}
            type="submit"
            disabled={isSubmitting}
          >
            Submit
          </button>
        )}
        <Text
          sx={{
            position: 'absolute',
            bottom: [-20, -32],
            left: '50%',
            transform: 'translateX(-50%)',
            color: 'error',
            whiteSpace: 'nowrap',
          }}
        >
          {submitError}
        </Text>
      </form>
    </Box>
  );
};

export default TeamSalesForm;
